import {
  AdministeredProductCategoryType,
  AdministeredProductMacroCategory,
  CoOwner,
  DossierFilter,
  FinancialInsuranceProductCategory,
  FinancialInsuranceProductMacroCategory,
  LiquidityProductCategoryType,
  LiquidityProductMacroCategory,
  ManagedFundsProductUnderlying,
  ManagedFundsUnderlyingGroup,
  ManagedProductCategory,
  ManagedProductMacroCategory,
  Product,
  ProductCategoryType,
  ProductDetails,
  ProductMacroCategory,
  ProductPerformanceList,
  ProductTransaction,
  ProductUnderlyingGroup,
  ProductUnderlyingType,
  RealEconomyProductCategory,
  RealEconomyProductCategoryType,
  RealEconomyProductMacroCategory,
  TotalFiaProductMacroCategory,
  TotalPortfolioProductMacroCategory,
  TraditionalInsuranceProductCategoryType,
  TraditionalInsuranceProductMacroCategory,
  UnitLinkedProductUnderlying,
  UnitLinkedUnderlyingGroup,
  Wallet,
  WalletSummary,
  WelfareProductCategoryType,
  WelfareProductMacroCategory,
} from '../../model';
import { ProductUnderlyingGroupsPaginated } from '../../model/wallet/details/product-underlying-groups-paginated';
import {
  PortfolioSummaryByMacroCategory,
  PortfolioMacroCategory,
  CategorySummary,
  ResidueDuration,
  RiskProfile,
  RiskProfileCheck,
  RealEconomyProduct,
} from '../../model/wallet/wallet-ow';
import { defaultPagination } from '../../utils/constants';
import {
  array2PaginatedMap,
  underlying2PaginatedMap,
} from '../../utils/pagination-utils';
import {
  Amministrativo,
  AssicurativoFinanziario,
  AssicurativoTradizionale,
  CategoriaUnitLinked,
  CategoriaUnitLinkedPrezzo,
  CategoriePolizzeTradizionaliRivalutate,
  DossierItem,
  EconomiaReale,
  Gestito,
  Liquidita,
  MacroCategorie,
  Performance,
  Portafoglio,
  PortfolioSummary,
  PortfolioSummaryItem,
  Previdenza,
  ProdottoAmministrativo,
  ProdottoAssicurativoTradizionale,
  ProdottoEconomiaReale,
  ProdottoGestito,
  ProdottoLiquidita,
  ProdottoPolizzeTradizionaliRivalutate,
  ProdottoPrevidenza,
  ProdottoUnitLinked,
  ProdottoUnitLinkedPrezzo,
  Sottostanti,
  SottostantiGestito,
  SottostantiMacrocategoriaEnum,
  SottostantiUnitLinked,
  TotalFia,
  TotalePortafoglio,
  WrapperMovimenti,
} from '../generated';
import {
  Composition,
  GroupData,
  MonitoringResult,
} from '../generated/objectway-zenith';
import { parseServerAmount, parseServerDateTime } from './commons-api-parsers';

export type ProductMacroCategorySummaryType =
  | 'ManagedFinancialInsurance'
  | 'RealEconomy'
  | 'Welfare'
  | 'Administered'
  | 'Liquidity';

const mapPortfolioSummaryTypeToProductMacroCategorySummaryType: {
  [key in keyof Omit<
    PortfolioSummary,
    'totalePortafoglio'
  >]: ProductMacroCategorySummaryType;
} = {
  gestitoAssFin: 'ManagedFinancialInsurance',
  economiaReale: 'RealEconomy',
  previdenza: 'Welfare',
  amministrato: 'Administered',
  liquidita: 'Liquidity',
};

export const parsePortafoglioToWalletSummary = ({
  summary,
  elencoPosizioni,
}: Portafoglio): WalletSummary => {
  const keys: (keyof Omit<PortfolioSummary, 'totalePortafoglio'>)[] = [
    'gestitoAssFin',
    'economiaReale',
    'previdenza',
    'amministrato',
    'liquidita',
  ];
  return {
    totalAmount: parseServerAmount(elencoPosizioni?.totalePortafoglio?.totaleComplessivo ?? { valore: 0, divisa: '€' }),
    macroCategories: keys.flatMap((key) => ({
      type: mapPortfolioSummaryTypeToProductMacroCategorySummaryType[key],
      amount:
        summary[key] &&
        (summary[key] as PortfolioSummaryItem)?.controvalore?.valore
          ? parseServerAmount(
              (summary[key] as PortfolioSummaryItem).controvalore
            )
          : {
              value:
                key === 'liquidita' || key === 'totalFia'
                  ? summary[key]
                  : undefined,
              currency: 'EUR',
            },
      gain:
        summary[key] &&
        (summary[key] as PortfolioSummaryItem)?.rendimentoPercentuale
          ? (summary[key] as PortfolioSummaryItem).rendimentoPercentuale
          : key === 'liquidita' || key === 'totalFia'
          ? undefined
          : 0,
    })),
  };
};

const mapSottostantiMacrocategoriaEnumToProductUnderlyingType = (
  value: SottostantiMacrocategoriaEnum
): ProductUnderlyingType => {
  switch (value) {
    case SottostantiMacrocategoriaEnum.Gestito:
      return 'ManagedFunds';
    case SottostantiMacrocategoriaEnum.UnitLinked:
      return 'UnitLinked';
  }
};

export const parseDettaglioProdottoToProductUnderlying = (
  sottostanti: Sottostanti
): ProductUnderlyingGroupsPaginated => {
  if (!sottostanti) {
    return null;
  }

  let underlyingGroupList: ProductUnderlyingGroup[];

  if (sottostanti.macrocategoria === SottostantiMacrocategoriaEnum.Gestito) {
    const sottostantiManaged = sottostanti as SottostantiGestito;
    underlyingGroupList = sottostantiManaged.sottostantiGestito.map((item) => {
      return {
        type: 'ManagedFunds',
        name: item.categoria,
        underlying: item.sottostanti.map((sottostante) => ({
          type: 'ManagedFunds',
          productName: sottostante.prodotto,
          quantity: sottostante.quantita,
          averagePurchasePrice: parseServerAmount(
            sottostante.prezzoMedioDiCarico
          ),
          averagePurchaseExchange: sottostante.cambioMedioDiCarico,
          rate: sottostante.rateo,
          marketPrice: parseServerAmount(sottostante.prezzoDiMercato),
          marketExchange: sottostante.cambioDiMercato,
          equivalent: parseServerAmount(sottostante.controvalore),
          incPercentage: sottostante.percentualeInc,
          riskCoefficientPercentage: sottostante.percentualeCoeffRischio,
        })) as ManagedFundsProductUnderlying[],
      };
    }) as ManagedFundsUnderlyingGroup[];
  } else {
    const sottostantiUnitLinked = sottostanti as SottostantiUnitLinked;
    underlyingGroupList = sottostantiUnitLinked.sottostantiUnitLinked.map(
      (item) => {
        return {
          type: 'UnitLinked',
          name: item.categoria,
          underlying: item.sottostanti.map((sottostante) => ({
            type: 'UnitLinked',
            productName: sottostante.prodotto,
            quantity: sottostante.quantita,
            averagePurchasePrice: parseServerAmount(
              sottostante.prezzoMedioDiCarico
            ),
            marketPrice: parseServerAmount(sottostante.prezzoDiMercato),
            equivalent: parseServerAmount(sottostante.controvalore),
            percentage: sottostante.percentuale,
            marketPriceDate: parseServerDateTime(
              sottostante.dataPrezzoDiMercato
            ),
          })) as UnitLinkedProductUnderlying[],
        };
      }
    ) as UnitLinkedUnderlyingGroup[];
  }

  return {
    type: mapSottostantiMacrocategoriaEnumToProductUnderlyingType(
      sottostanti.macrocategoria
    ),
    equivalent: parseServerAmount(sottostanti.totale),
    underlyingGroupsPaginated: underlying2PaginatedMap(underlyingGroupList, {
      ...defaultPagination,
    }),
  };
};

export const parseDettaglioProdottoToProductDetails = (
  wrapperMovimenti: WrapperMovimenti
): Omit<ProductDetails, 'contract' | 'category' | 'underlyingGroups'> => {
  const transactions: ProductTransaction[] =
    wrapperMovimenti?.movimenti?.map((movimento) => ({
      operationDate: parseServerDateTime(movimento.dataOperazione),
      settlementDate: parseServerDateTime(movimento.dataRegolamento),
      valueDate: parseServerDateTime(movimento.dataValuta),
      category: movimento.categoria as ProductCategoryType,
      reason: movimento.causale,
      amount: parseServerAmount(movimento.importoRegolato),
      fees: parseServerAmount(movimento.commissioni),
      expenses: parseServerAmount(movimento.spese),
      bolli: parseServerAmount(movimento.bolli),
      equivalent: parseServerAmount(movimento.controvalore),
      quantity: movimento.quantita,
      price: parseServerAmount(movimento.prezzo),
      exchange: movimento.cambio,
      priceCurrency: parseServerAmount(movimento.prezzoDivisa),
      exchangeDate: parseServerDateTime(movimento.dataCambio),
    })) || [];

  const result = array2PaginatedMap<ProductTransaction>(transactions, {
    ...defaultPagination,
  });

  return {
    transactionsPaginated: result,
    company: wrapperMovimenti.info?.societaProdotto,
    group: wrapperMovimenti.info?.prodottoGruppo,
    subProduct: wrapperMovimenti.info?.sottoprodotto,
    ISIN: wrapperMovimenti.info?.isin,
    RP: wrapperMovimenti.info?.codiceRP,
  };
};

export const parsePerformanceProdottiToProductsPerformances = (
  performance: Performance
): ProductPerformanceList => {
  if (!performance?.elementi) {
    return {
      totalEquivalent: parseServerAmount({ valore: 0 }),
      totalGain: 0,
      products: [],
    };
  }

  return {
    totalEquivalent: parseServerAmount(performance.totaleImporto),
    totalGain: performance.totaleRendimento,
    products: performance.elementi.map((item) => ({
      flagSGR: item.prodottoOggettoDiFusione,
      description: item.descrizioneProdotto,
      type: item.tipoProdotto,
      contract: item.contratto,
      investment: item.investito,
      switchIn: item.switchIn,
      withdrawalAndDividends: item.prelevatoEDividendi,
      switchOut: item.switchOut,
      averageDeposits: item.giacenzaMedia,
      amount: item.importo,
      plusMinus: item.plusMinus,
      gain: item.rendimento,
      flagFiaMultirichiamo: item.isFiaMultirichiamo,
      isMergingFund: item.isMergingFund,
      /* TODO: in attesa di APC-640
      nav,
      averagePurchasePrice,
      rendimentoAnnualizzato,
      */
    })),
  };
};

// cointestatariDettaglio ha anche il codice cliente (AZUAT-13916); senza, si usano i soli nominativi
const parseCoOwners = (item: DossierItem): CoOwner[] | undefined => {
  if (item.cointestatariDettaglio?.length) {
    return item.cointestatariDettaglio.map((coOwner) => ({
      name: coOwner.nominativo,
      code: coOwner.codiceCliente,
    }));
  }
  return item.cointestatari?.map((name) => ({ name }));
};

export const parseDossierToDossierFilter = (
  dossier: DossierItem[]
): DossierFilter => {
  return {
    dossier: dossier.map((item) => ({
      code: item.codiceDossier,
      flagOpen: item.aperto,
      flagSelected: true,
      flagIsOwner: item.utenteIsPrimoIntestatario,
      consultingType: item.tipoConsulenza
        ? { type: item.tipoConsulenza, contract: item.numeroContrattoServizio }
        : undefined,
      subDossier: item.sottoDossier?.map((subDossier) => ({
        contract: subDossier.codiceContratto,
        suppCompanyRefNum: subDossier.suppCompanyRefNum,
        flagSelected: true,
        macroProductName: subDossier.macroProdotto,
        productName: subDossier.prodotto,
        controvalore: subDossier.controvalore
          ? parseServerAmount(subDossier.controvalore)
          : undefined,
        commissionRegimeTypeDescription:
          subDossier.commissionRegimeTypeDescription,
      })),
      coOwner: parseCoOwners(item),
      controvalore: (item as any).controvalore
        ? parseServerAmount((item as any).controvalore)
        : undefined,
      commissionRegimeTypeDescription: (item as any)
        .commissionRegimeTypeDescription,
    })),
  };
};

const parseBaseProduct = (
  prodotto:
    | ProdottoGestito
    | ProdottoAmministrativo
    | ProdottoAssicurativoTradizionale
    | ProdottoLiquidita
    | ProdottoPrevidenza
    | ProdottoPolizzeTradizionaliRivalutate
    | ProdottoUnitLinkedPrezzo
    | ProdottoUnitLinked
    | ProdottoEconomiaReale
): Product => ({
  productName: prodotto.prodotto,
  contract: prodotto.contratto,
  newContractCode: prodotto.suppCompanyRefNum,
  titleHolders: prodotto.cointestatari,
  productCode: prodotto.code,
  isin: prodotto.isin,
  isPriips: prodotto.priips ?? false,
  isKidExisting: prodotto.esistenzaKid ?? false,
  rp: prodotto.rp,
});

const parseGestitoToManagedProductCategory = (
  gestito: Gestito
): ManagedProductMacroCategory => ({
  type: 'Managed',
  amount: parseServerAmount(gestito.totale),
  categories: gestito.categorie.map(
    (categoria) =>
      ({
        type: categoria.nome,
        totalAmount: parseServerAmount(categoria.totale),
        macroProducts: categoria.macroProdotti.map((macroProdotto) => ({
          name: macroProdotto.nome,
          totalAmount: parseServerAmount(macroProdotto.totale),
          products: macroProdotto.prodotti.map((prodotto) => ({
            ...parseBaseProduct(prodotto),
            quantity: prodotto.quantita,
            currency: prodotto.divisa,
            averagePurchasePrice: parseServerAmount(
              prodotto.prezzoMedioDiCarico
            ),
            averagePurchaseExchange: prodotto.caricoMedioCambio,
            currencyShareValue: parseServerAmount(prodotto.valoreQuotaDivisa),
            exchange: prodotto.cambio,
            date: parseServerDateTime(prodotto.data),
            equivalent: parseServerAmount(prodotto.controvalore),
            gainEur: parseServerAmount(prodotto.rendimentoValuta),
            gainPercentage: prodotto.rendimentoPercentuale ?? 'N/D',
            flagSGR: prodotto.disclaimer,
          })),
        })),
      } as ManagedProductCategory)
  ),
});

const parseCategoriaUnitLinkedPrezzoToUnitLinkedAtPriceCategory = (
  unitLinkedPrezzo: CategoriaUnitLinkedPrezzo
): FinancialInsuranceProductCategory => ({
  type: 'UnitLinkedAtPrice',
  totalAmount: parseServerAmount(unitLinkedPrezzo.totale),
  products: unitLinkedPrezzo.prodotti.map((prodotto) => ({
    ...parseBaseProduct(prodotto),
    date: parseServerDateTime(prodotto.data),
    equivalent: parseServerAmount(prodotto.controvalore),
    gainEur: parseServerAmount(prodotto.rendimento),
    gainPercentage: prodotto.rendimentoPercentuale ?? 'N/D',
    quantity: prodotto.quantita,
    currency: prodotto.divisa,
    averagePurchasePrice: parseServerAmount(prodotto.prezzoMedioDiCarico),
    averagePurchaseExchange: prodotto.caricoMedioCambio,
    currencyShareValue: parseServerAmount(prodotto.valoreQuotaDivisa),
    exchange: prodotto.cambio,
  })),
});

const parseCategoriaUnitLinkedToUnitLinkedCategory = (
  unitLinked: CategoriaUnitLinked
): FinancialInsuranceProductCategory => ({
  type: 'UnitLinked',
  totalAmount: parseServerAmount(unitLinked.totale),
  products: unitLinked.prodotti.map((prodotto) => ({
    ...parseBaseProduct(prodotto),
    date: parseServerDateTime(prodotto.data),
    equivalent: parseServerAmount(prodotto.controvalore),
    gainEur: parseServerAmount(prodotto.rendimento),
    gainPercentage: prodotto.rendimentoPercentuale ?? 'N/D',
  })),
});

const parseCategoriePolizzeTradizionaliRivalutateToTraditionalPoliciesRevaluedCategory =
  (
    polizze: CategoriePolizzeTradizionaliRivalutate
  ): FinancialInsuranceProductCategory => ({
    type: 'TraditionalPoliciesRevalued',
    totalAmount: parseServerAmount(polizze.totale),
    products: polizze.prodotti.map((prodotto) => ({
      ...parseBaseProduct(prodotto),
      code: prodotto.codice,
      equivalent: parseServerAmount(prodotto.controvalore),
    })),
  });

const parseCategoriaAssicurativoFinanziarioToFinancialInsuranceProductCategory =
  (
    key: keyof Omit<AssicurativoFinanziario, 'totale'>,
    categoria:
      | CategoriaUnitLinkedPrezzo
      | CategoriePolizzeTradizionaliRivalutate
      | CategoriaUnitLinked
  ): FinancialInsuranceProductCategory => {
    switch (key) {
      case 'unitLinkedPrezzo':
        return parseCategoriaUnitLinkedPrezzoToUnitLinkedAtPriceCategory(
          categoria as CategoriaUnitLinkedPrezzo
        );
      case 'unitLinked':
        return parseCategoriaUnitLinkedToUnitLinkedCategory(
          categoria as CategoriaUnitLinked
        );
      case 'polizzeTradizionaliRivalutate':
        return parseCategoriePolizzeTradizionaliRivalutateToTraditionalPoliciesRevaluedCategory(
          categoria as CategoriePolizzeTradizionaliRivalutate
        );
    }
  };

const parseAssicurativoFinanziarioToFinancialInsuranceProductMacroCategory = (
  assicurativoFinanziario: AssicurativoFinanziario
): FinancialInsuranceProductMacroCategory => {
  const keys: (keyof Omit<AssicurativoFinanziario, 'totale'>)[] = [
    'unitLinked',
    'unitLinkedPrezzo',
    'polizzeTradizionaliRivalutate',
  ];
  return {
    type: 'FinancialInsurance',
    amount: parseServerAmount(assicurativoFinanziario.totale),
    categories: keys.flatMap((key) =>
      assicurativoFinanziario[key]
        ? parseCategoriaAssicurativoFinanziarioToFinancialInsuranceProductCategory(
            key,
            assicurativoFinanziario[key]
          )
        : []
    ),
  };
};

const parseLiquiditaToLiquidityProductMacroCategory = (
  macroCategoria: Liquidita
): LiquidityProductMacroCategory => ({
  type: 'Liquidity',
  amount: parseServerAmount(macroCategoria.totale),
  categories: macroCategoria.categorie.map((categoria) => ({
    totalAmount: parseServerAmount(categoria.totale),
    totaleSaldoContabile: parseServerAmount(categoria.totaleSaldoContabile),
    totaleSaldoDisponibile: parseServerAmount(categoria.totaleSaldoDisponibile),
    totaleSaldoLiquido: parseServerAmount(categoria.totaleSaldoLiquido),
    type: categoria.nome as LiquidityProductCategoryType,
    products: categoria.prodotti.map((prodotto) => ({
      ...parseBaseProduct(prodotto),
      accountBalance: parseServerAmount(prodotto.saldoContabile),
      accountBalanceDate: parseServerDateTime(prodotto.dataSaldoContabile),
      cashBalance: parseServerAmount(prodotto.saldoLiquido),
      cashBalanceDate: parseServerDateTime(prodotto.dataSaldoLiquido),
      availableBalance: parseServerAmount(prodotto.saldoDisponibile),
      availableBalanceDate: parseServerDateTime(prodotto.dataSaldoDisponibile),
    })),
  })),
});

const parsePrevidenzaToWelfareProductMacroCategory = (
  macroCategoria: Previdenza
): WelfareProductMacroCategory => ({
  type: 'Welfare',
  amount: parseServerAmount(macroCategoria.totale),
  categories: macroCategoria.categorie.map((categoria) => ({
    totalAmount: parseServerAmount(categoria.totale),
    type: categoria.nome as WelfareProductCategoryType,
    products: categoria.prodotti.map((prodotto) => ({
      ...parseBaseProduct(prodotto),
      date: parseServerDateTime(prodotto.data),
      equivalent: parseServerAmount(prodotto.controvalore),
      itemsInTransit: parseServerAmount(prodotto.partiteViaggianti),
      balancePosition: parseServerAmount(prodotto.saldoPosizione),
    })),
  })),
});

const parseAmministrativoToAdministeredProductMacroCategory = (
  macroCategoria: Amministrativo
): AdministeredProductMacroCategory => ({
  type: 'Administered',
  amount: parseServerAmount(macroCategoria.totale),
  categories: macroCategoria.categorie.map((categoria) => ({
    totalAmount: parseServerAmount(categoria.totale),
    type: categoria.nome as AdministeredProductCategoryType,
    products: categoria.prodotti.map((prodotto) => ({
      ...parseBaseProduct(prodotto),
      quantity: prodotto.quantita,
      currency: prodotto.divisa,
      currencyShareValue: parseServerAmount(prodotto.valoreQuotaDivisa),
      exchange: prodotto.cambio,
      date: parseServerDateTime(prodotto.data),
      equivalent: parseServerAmount(prodotto.controvalore),
      gainEur: parseServerAmount(prodotto.rendimento),
      gainPercentage: prodotto.rendimentoPercentuale ?? 'N/D',
    })),
  })),
});

const parseAssicurativoTradizionaleToLiquidityProductMacroCategory = (
  macroCategoria: AssicurativoTradizionale
): TraditionalInsuranceProductMacroCategory => ({
  type: 'TraditionalInsurance',
  amount: undefined,
  categories:
    macroCategoria?.categorie?.map((categoria) => ({
      totalAmount: parseServerAmount(categoria.totale),
      type: categoria.nome as TraditionalInsuranceProductCategoryType,
      products:
        categoria?.prodotti?.map((prodotto) => ({
          ...parseBaseProduct(prodotto),
          totalPremiumsPaid: parseServerAmount(prodotto.totalePremiVersati),
          date: parseServerDateTime(prodotto.data),
        })) ?? [],
    })) ?? [],
});

const parseEconomiaRealeToRealEconomyProductMacroCategory = (
  macroCategoria: EconomiaReale
): RealEconomyProductMacroCategory => ({
  type: 'RealEconomy',
  amount: parseServerAmount(macroCategoria.totale),
  commitment: parseServerAmount(macroCategoria.commitment),
  quoteEmissione: parseServerAmount(macroCategoria.quoteEmissione),
  categories: macroCategoria.categorie.map(
    (categoria) =>
      ({
        totalAmount: parseServerAmount(categoria.totale),
        type: categoria.nome as RealEconomyProductCategoryType,
        macroProducts: categoria.macroProdotti.map((macroProdotto) => ({
          name: macroProdotto.nome,
          totalAmount: parseServerAmount(macroProdotto.totale),
          products: macroProdotto.prodotti.map((prodotto) => ({
            ...parseBaseProduct(prodotto),
            paidValidated: parseServerAmount(prodotto.versatoAvvalorato),
            date: parseServerDateTime(prodotto.data),
            impegni: parseServerAmount(prodotto.commitment),
            counterValue: parseServerAmount(prodotto.controvalore),
            capitalEstimates: parseServerAmount(prodotto.proventi),
            obligation: parseServerAmount(prodotto.commitment),
            paidToBeValidated: parseServerAmount(prodotto.versatoDaAvvalorare),
          })),
        })),
      } as RealEconomyProductCategory)
  ),
});

const parseTotalFiaToTotalFiaProductMacroCategory = (
  macroCategoria: TotalFia
): TotalFiaProductMacroCategory => ({
  type: 'TotalFia',
  amount: parseServerAmount(macroCategoria.totale),
  categories: [],
});

const parseTotalPortfolioToTotalPortfolioProductMacroCategory = (
  macroCategoria: TotalePortafoglio
): TotalPortfolioProductMacroCategory => ({
  type: 'TotalPortfolio',
  amount: parseServerAmount(macroCategoria.totaleComplessivo),
  categories: [],
});

const parseMacroCategoriaToMacroCategory = (
  key: keyof MacroCategorie,
  macroCategoria:
    | Gestito
    | AssicurativoFinanziario
    | Previdenza
    | Amministrativo
    | Liquidita
    | AssicurativoTradizionale
    | EconomiaReale
    | TotalFia
    | TotalePortafoglio
): ProductMacroCategory => {
  switch (key) {
    case 'gestito':
      return parseGestitoToManagedProductCategory(macroCategoria as Gestito);
    case 'assicurativoFinanziario':
      return parseAssicurativoFinanziarioToFinancialInsuranceProductMacroCategory(
        macroCategoria as AssicurativoFinanziario
      );
    case 'liquidita':
      return parseLiquiditaToLiquidityProductMacroCategory(
        macroCategoria as Liquidita
      );
    case 'previdenza':
      return parsePrevidenzaToWelfareProductMacroCategory(
        macroCategoria as Previdenza
      );
    case 'amministrativo':
      return parseAmministrativoToAdministeredProductMacroCategory(
        macroCategoria as Amministrativo
      );
    case 'assicurativoTradizionale':
      return parseAssicurativoTradizionaleToLiquidityProductMacroCategory(
        macroCategoria as AssicurativoTradizionale
      );
    case 'economiaReale':
      return parseEconomiaRealeToRealEconomyProductMacroCategory(
        macroCategoria as EconomiaReale
      );
    case 'totalFia':
      return parseTotalFiaToTotalFiaProductMacroCategory(
        macroCategoria as TotalFia
      );
    case 'totalePortafoglio':
      return parseTotalPortfolioToTotalPortfolioProductMacroCategory(
        macroCategoria as TotalePortafoglio
      );
  }
};

const showMacroCategory = (
  key: keyof MacroCategorie,
  macroCategoria:
    | Gestito
    | AssicurativoFinanziario
    | Previdenza
    | Amministrativo
    | Liquidita
    | AssicurativoTradizionale
    | EconomiaReale
): boolean => {
  if (key === 'totalFia' || key === 'totalePortafoglio') {
    return true;
  }

  if (key === 'economiaReale') {
    const ecoReale = macroCategoria as EconomiaReale;
    return !!(ecoReale.totale?.valore || ecoReale.commitment?.valore);
  }

  if (key !== 'assicurativoTradizionale') {
    return !!(
      macroCategoria as
        | Gestito
        | Previdenza
        | Amministrativo
        | Liquidita
        | AssicurativoFinanziario
    ).totale?.valore;
  }

  return (macroCategoria as AssicurativoTradizionale).categorie?.length > 0;
};

export const parsePortafoglioToWallet = (portafoglio: Portafoglio): Wallet => {
  const keys: (keyof MacroCategorie)[] = [
    'gestito',
    'assicurativoFinanziario',
    'previdenza',
    'amministrativo',
    'liquidita',
    'assicurativoTradizionale',
    'economiaReale',
    'totalFia',
    'totalePortafoglio',
  ];
  return {
    macroCategories: keys.flatMap((key) =>
      portafoglio.elencoPosizioni[key] &&
      showMacroCategory(key, portafoglio.elencoPosizioni[key])
        ? parseMacroCategoriaToMacroCategory(
            key,
            portafoglio.elencoPosizioni[key]
          )
        : []
    ),
    performances: undefined,
  };
};

export function parseCompositionToPortfolioSummary(
  composition: Composition
): PortfolioSummaryByMacroCategory {
  return {
    totalAmount: { value: composition.total, currency: 'EUR' },
    macrocategory: parseGroupsToMacrocategorySummary(
      composition.groups
      // composition.total
    ),
  };
}

export const parseCompositionToRealEconomyAssetSummary = (
  composition: Composition
) => {
  return {
    totalAmount: { value: composition.total, currency: 'EUR' },
    category: Object.values(composition.groups).map(
      (val) =>
        ({
          type: val.description,
          quote: val.percentage,
        } as CategorySummary)
    ),
  };
};

export const parseCompositionToRealEconomyProductConcentration = (
  composition: Composition
) => {
  return {
    products: Object.values(composition.groups).map((val) => ({
      quote: val.percentage,
      label: val.description,
    })) as RealEconomyProduct[],
  };
};

function parseGroupsToMacrocategorySummary(groups: {
  [key: string]: GroupData;
}): PortfolioMacroCategory[] {
  return Object.entries(groups).map((entry) => {
    return {
      code: entry[1].code,
      description: entry[1].description,
      percentage: entry[1].percentage,
      value: entry[1].value,
    };
  });
}
export function parseResidueDuration(result: Composition): ResidueDuration {
  return {
    products: Object.entries(result.groups).map((entry) => {
      return {
        description: entry[1].description,
        code: entry[1].code,
        years: entry[1].value,
        expirationPercentage: entry[1].percentage,
      };
    }),
  };
}
export function parseRiskProfile(result: MonitoringResult): RiskProfile {
  return {
    checks: result.checkResults.map(
      (checkResult) =>
        ({
          isLimitRespected: checkResult.resultCode === 'OK',
          thresholdValue: checkResult.thresholdValue,
          value: checkResult.value,
          type: checkResult.checkType,
        } as RiskProfileCheck)
    ),
  };
}
